/**
 * Pure text helpers for the admin "Equipo" tab: the reminder an admin pastes
 * to one person, the list of everyone who is behind, and the small Spanish
 * formatters both need. Kept free of React and of the clipboard so the exact
 * copy can be unit-tested.
 */

import type { ImputacionLevel, UserImputationSummary } from '../../../types/imputacion'

const MONTHS = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

/** "septiembre" for month 9 (1-based). */
export const monthName = (month: number): string => MONTHS[month - 1] ?? String(month)

/** "Septiembre" for month 9: the form used in titles and the month switcher. */
export const monthTitle = (month: number): string => {
    const name = monthName(month)
    return name.charAt(0).toUpperCase() + name.slice(1)
}

/** Hours in Spanish notation without the unit: 8.5 → "8,5", 17 → "17". */
export const fmtHours = (n: number | null | undefined): string => {
    const v = Math.round((n ?? 0) * 100) / 100
    return String(v).replace('.', ',')
}

/** "03/09" from an ISO date, parsed by hand so the browser time zone cannot shift the day. */
export const fmtDayMonth = (iso: string): string => {
    const [, mm, dd] = iso.slice(0, 10).split('-')
    return `${dd}/${mm}`
}

/** Day number of an ISO date: "2026-09-03" → 3. */
const dayOfMonth = (iso: string): number => Number(iso.slice(8, 10))

/** Spanish list joining: [3] → "3"; [3, 4] → "3 y 4"; [3, 4, 14] → "3, 4 y 14". */
export const joinEs = (items: string[]): string => {
    if (items.length <= 1) return items.join('')
    return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`
}

/** "día 3" / "días 3, 4 y 14", or '' when there are none. */
export const daysPhrase = (isoDays: string[]): string => {
    if (!isoDays.length) return ''
    const nums = isoDays.map(dayOfMonth).sort((a, b) => a - b).map(String)
    return `${nums.length === 1 ? 'día' : 'días'} ${joinEs(nums)}`
}

/**
 * Compact age of a timestamp: "unos segundos", "12 min", "3 h", "2 d".
 * Returns null when there is no timestamp (or it does not parse), so callers
 * can hide the "hace …" text instead of printing nonsense.
 */
export const ageText = (iso: string | null | undefined, now: Date = new Date()): string | null => {
    if (!iso) return null
    const t = new Date(iso).getTime()
    if (Number.isNaN(t)) return null
    const seconds = Math.max(0, Math.round((now.getTime() - t) / 1000))
    if (seconds < 60) return 'unos segundos'
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} h`
    return `${Math.floor(seconds / 86400)} d`
}

/** Link to the person's own Imputación screen, already on the month being chased. */
export const imputacionUrl = (origin: string, year: number, month: number): string =>
    `${origin}/imputacion?year=${year}&month=${month}`

/** Greeting name: the first word of the display name ("Gonzalo García" → "Gonzalo"). */
const firstName = (name: string): string => name.trim().split(/\s+/)[0] || name

// Levels whose figure is trustworthy enough to send a "te faltan X h" message.
// Grey levels (no Redmine data, no working day configured, shared Redmine id)
// would put a wrong number in front of the person.
const REMINDABLE: ImputacionLevel[] = ['al_dia', 'retrasado', 'critico', 'posible_retraso']

// Same tolerance as the backend (uc.TOLERANCE_HOURS): below it the person is "al día".
const TOLERANCE_HOURS = 0.5

/**
 * Text for [Copiar recordatorio], or null when there is nothing honest to
 * remind (on track, or the figure is not reliable).
 *
 * People without a Redmine link get the linking message instead: the Manager
 * pins their Redmine id the first time they open Imputación with their own login.
 */
export const reminderText = (
    user: UserImputationSummary,
    year: number,
    month: number,
    origin: string,
): string | null => {
    const name = firstName(user.user_name)
    const url = imputacionUrl(origin, year, month)
    if (user.shared_redmine_user) return null
    if (!user.mapped) {
        return `Hola ${name}, abre Imputación con tu usuario para que se vincule tu cuenta de Redmine: ${url}`
    }
    if (!REMINDABLE.includes(user.totals.level)) return null
    const missing = user.totals.missing_to_date
    if (missing <= TOLERANCE_HOURS) return null
    const days = daysPhrase(user.totals.days_without_entries ?? [])
    const daysPart = days ? ` (${days})` : ''
    return `Hola ${name}, te faltan ${fmtHours(missing)} h por imputar en ${monthName(month)}${daysPart}. Puedes hacerlo en ${url}`
}

/** Levels that go into [Copiar lista de pendientes], with their plain-text label. */
export const PENDING_LABELS: Partial<Record<ImputacionLevel, string>> = {
    critico: 'Crítico',
    retrasado: 'Retrasado',
    posible_retraso: 'Posible retraso',
}

/** People that [Copiar lista de pendientes] includes, most hours missing first. */
export const pendingUsers = (users: UserImputationSummary[]): UserImputationSummary[] =>
    users
        .filter((u) => u.totals.level in PENDING_LABELS)
        .sort((a, b) => b.totals.missing_to_date - a.totals.missing_to_date)

/**
 * Text for [Copiar lista de pendientes]: one line per person who is Retrasado,
 * Crítico or Posible retraso, plus the link. '' when nobody is behind.
 */
export const pendingListText = (
    users: UserImputationSummary[],
    year: number,
    month: number,
    origin: string,
): string => {
    const pending = pendingUsers(users)
    if (!pending.length) return ''
    const lines = pending.map((u) => {
        const days = daysPhrase(u.totals.days_without_entries ?? [])
        const daysPart = days ? ` · ${days}` : ''
        return `• ${u.user_name}: faltan ${fmtHours(u.totals.missing_to_date)} h · ${PENDING_LABELS[u.totals.level]}${daysPart}`
    })
    return [
        `Pendientes de imputar en ${monthName(month)} de ${year}:`,
        ...lines,
        `Se imputa en ${imputacionUrl(origin, year, month)}`,
    ].join('\n')
}
